import { Outlet, NavLink, Link } from "react-router-dom";
import { useState } from "react";
import { BookOpen, HelpCircle, Menu, X, Sun, Moon, ArrowLeft } from "lucide-react";
import { useTheme } from "@/contexts/theme-context";
import { cn } from "@/utils";

export const DocsLayout = () => {
  const { theme, toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isDark = theme === "dark";

  const navigation = [
    { name: "FAQs", to: "/docs/faq", icon: HelpCircle },
    { name: "Integrations", to: "/docs/integrations", icon: BookOpen },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-(--sidebar-width) bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex flex-col h-full transition-all duration-300 lg:static lg:translate-x-0",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo Section */}
        <div className="p-5 flex items-center justify-between border-b border-slate-100 dark:border-slate-900">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/brand/light-logo-with-text.svg" alt="Argus" className="h-7 w-auto dark:hidden" />
            <img src="/brand/dark-logo-with-text.svg" alt="Argus" className="h-7 w-auto hidden dark:block" />
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sidebar Nav */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          <div className="px-3 py-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
            Documentation
          </div>
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.to}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-colors",
                  isActive
                    ? "bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:text-orange-400 font-semibold"
                    : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-900"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      "w-4 h-4 mr-3 transition-colors",
                      isActive ? "text-orange-500 dark:text-orange-400" : "text-slate-400"
                    )}
                  />
                  <span>{item.name}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-900 bg-slate-50/50 dark:bg-slate-900/20 space-y-4">
          {/* Dark Mode Switcher */}
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center text-sm font-medium text-slate-600 dark:text-slate-400">
              {isDark ? <Moon className="w-4 h-4 mr-3" /> : <Sun className="w-4 h-4 mr-3" />}
              Dark mode
            </div>
            <button
              onClick={toggleTheme}
              className={cn(
                "w-10 h-5 rounded-full relative transition-colors duration-200",
                isDark ? "bg-orange-500" : "bg-slate-200 dark:bg-slate-800"
              )}
            >
              <div
                className={cn(
                  "absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200",
                  isDark ? "translate-x-5" : "translate-x-0.5"
                )}
              />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Container */}
      <main className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 lg:px-6 shrink-0 relative z-30">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-md transition-colors text-slate-500"
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-400">
              Documentation
            </span>
          </div>

          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors text-xs font-semibold text-slate-700 dark:text-slate-300"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Portal Login
            </Link>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto no-scrollbar">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DocsLayout;
